import { findProcessedAudioSeconds, getRecordingStatusDisplay, isSegmentActivelyRecording, } from 'lib/event-state-merge';
import { formatElapsedSeconds } from 'lib/format-duration';
const getRecordingStatusDisplayPresentation = (display, telemetry, segmentId, intl) => {
    switch (display.kind) {
        case 'running': {
            const processedSeconds = findProcessedAudioSeconds(telemetry, segmentId) ?? 0;
            return {
                led: 'ok',
                message: intl.formatMessage({ id: 'recordingStatusRunningWithDuration' }, { duration: formatElapsedSeconds(processedSeconds) }),
            };
        }
        case 'stalled':
            return {
                led: 'warning',
                message: intl.formatMessage({ id: 'recordingStatusStalled' }),
            };
        case 'networkIssue':
            return {
                led: 'warning',
                message: intl.formatMessage({ id: 'recordingStatusNetworkIssue' }),
            };
        case 'offline':
            return {
                led: 'error',
                message: intl.formatMessage({ id: 'recordingStatusOffline' }, { duration: formatElapsedSeconds(display.seconds) }),
            };
        case 'noAudio':
            return {
                led: 'warning',
                message: intl.formatMessage({ id: 'recordingStatusNoAudio' }, { duration: formatElapsedSeconds(display.seconds) }),
            };
        case 'audioConfig': {
            const message = (() => {
                switch (display.reason) {
                    case 'noSources':
                        return intl.formatMessage({ id: 'recordingStatusAddAudioSource' });
                    case 'noSourceSelected':
                        return intl.formatMessage({ id: 'recordingStatusSelectAudioSource' });
                    case 'noMicrophone':
                        return intl.formatMessage({ id: 'recordingStatusSelectMicrophone' });
                    case 'audioMuted':
                        return intl.formatMessage({ id: 'recordingStatusAudioMuted' });
                    default:
                        return intl.formatMessage({ id: 'recordingStatusReady' });
                }
            })();
            return {
                led: display.reason === 'audioMuted' ? 'warning' : 'error',
                message,
            };
        }
        default:
            return {
                led: 'ok',
                message: intl.formatMessage({ id: 'recordingStatusReady' }),
            };
    }
};
const shouldShowMainStatusTransport = (connectionHealth, connectionReady, eventSnapshot, selectedSegmentId) => (connectionReady || isSegmentActivelyRecording(eventSnapshot, selectedSegmentId))
    && (connectionHealth === 'offline' || connectionHealth === 'networkIssue');
export const shouldHideMainStatusSegmentTitle = shouldShowMainStatusTransport;
const shouldShowMainStatusOffline = (connectionHealth, connectionReady, eventSnapshot, selectedSegmentId) => shouldShowMainStatusTransport(connectionHealth, connectionReady, eventSnapshot, selectedSegmentId)
    && connectionHealth === 'offline';
const shouldShowMainStatusNetworkIssue = (connectionHealth, connectionReady, eventSnapshot, selectedSegmentId) => shouldShowMainStatusTransport(connectionHealth, connectionReady, eventSnapshot, selectedSegmentId)
    && connectionHealth === 'networkIssue';
const getOfflineStatusPresentation = (degradedSeconds, intl) => ({
    led: 'error',
    message: intl.formatMessage({ id: 'recordingStatusOffline' }, { duration: formatElapsedSeconds(degradedSeconds) }),
});
const getNetworkIssueStatusPresentation = (intl) => ({
    led: 'warning',
    message: intl.formatMessage({ id: 'recordingStatusNetworkIssue' }),
});
export const getSelectedSegmentStatusPresentation = ({ connectionHealth, connectionReady, degradedSeconds, eventSnapshot, telemetry, selectedSegmentId, selectedSegment, nowMs, intl, }) => {
    if (shouldShowMainStatusOffline(connectionHealth, connectionReady, eventSnapshot, selectedSegmentId)) {
        return getOfflineStatusPresentation(degradedSeconds, intl);
    }
    if (shouldShowMainStatusNetworkIssue(connectionHealth, connectionReady, eventSnapshot, selectedSegmentId)) {
        return getNetworkIssueStatusPresentation(intl);
    }
    const completed = getSelectedSegmentCompletedPresentation(selectedSegment, intl);
    if (completed !== null) {
        return completed;
    }
    const terminating = getSegmentTerminatingPresentation(selectedSegment, intl);
    if (terminating !== null) {
        return terminating;
    }
    const display = getRecordingStatusDisplay({
        connectionHealth,
        connectionReady,
        degradedSeconds,
        eventSnapshot,
        telemetry,
        segmentId: selectedSegmentId,
        nowMs,
    });
    if (display !== null) {
        return getRecordingStatusDisplayPresentation(display, telemetry, selectedSegmentId, intl);
    }
    return {
        led: 'ok',
        message: intl.formatMessage({ id: 'recordingStatusReady' }),
    };
};
export const getSegmentTerminatingPresentation = (segment, intl) => {
    if (segment?.status === 'completed' || segment?.stopReason == null) {
        return null;
    }
    return {
        led: 'warning',
        message: intl.formatMessage({ id: 'segmentTerminatingStatus' }),
    };
};
/** Persisted segment state only (`status` + `stopReason` from DB). */
export const getSegmentListStatusPresentation = (segment, intl) => {
    if (segment.status === 'completed') {
        switch (segment.stopReason) {
            case 'Error':
                return {
                    icon: 'closeError',
                    tooltip: intl.formatMessage({ id: 'segmentFailedStatus' }),
                };
            case 'IdleAudio':
                return {
                    icon: 'warning',
                    tooltip: intl.formatMessage({ id: 'segmentIdleAudioStatus' }),
                };
            case 'UserRequest':
                return {
                    icon: 'checkSuccess',
                    tooltip: intl.formatMessage({ id: 'segmentCompletedUserStatus' }),
                };
            default:
                return {
                    icon: 'checkMuted',
                    tooltip: intl.formatMessage({ id: 'segmentCompletedStatus' }),
                };
        }
    }
    if (segment.status === 'recording' && segment.stopReason != null) {
        return {
            icon: 'warning',
            tooltip: intl.formatMessage({ id: 'segmentTerminatingStatus' }),
        };
    }
    if (segment.status === 'recording') {
        return {
            icon: 'recording',
            tooltip: intl.formatMessage({ id: 'segmentRecordingStatus' }),
        };
    }
    return {
        icon: 'clock',
        tooltip: intl.formatMessage({ id: 'segmentCreatedStatus' }),
    };
};
export const getSelectedSegmentCompletedPresentation = (segment, intl) => {
    if (segment?.status !== 'completed') {
        return null;
    }
    switch (segment.stopReason) {
        case 'Error':
            return {
                led: 'error',
                message: intl.formatMessage({ id: 'segmentFailedStatus' }),
            };
        case 'IdleAudio':
            return {
                led: 'warning',
                message: intl.formatMessage({ id: 'segmentIdleAudioStatus' }),
            };
        default:
            return {
                led: 'muted',
                message: intl.formatMessage({ id: 'segmentCompletedStatus' }),
            };
    }
};
export const getMicStatusPresentation = (status, connectionHealth, intl) => {
    const offlineLed = connectionHealth === 'offline';
    if (status.kind === 'audioConfig') {
        const message = (() => {
            switch (status.reason) {
                case 'noSources':
                    return intl.formatMessage({ id: 'recordingStatusAddAudioSource' });
                case 'noSourceSelected':
                    return intl.formatMessage({ id: 'recordingStatusSelectAudioSource' });
                case 'noMicrophone':
                    return intl.formatMessage({ id: 'recordingStatusSelectMicrophone' });
                case 'audioMuted':
                    return intl.formatMessage({ id: 'recordingStatusAudioMuted' });
                default:
                    return intl.formatMessage({ id: 'audioSourcesTitle' });
            }
        })();
        const led = (() => {
            if (offlineLed) {
                return 'muted';
            }
            if (status.reason === 'audioMuted') {
                return 'warning';
            }
            return 'error';
        })();
        return {
            led,
            message,
        };
    }
    return {
        led: offlineLed ? 'muted' : 'ok',
        message: intl.formatMessage({ id: 'audioSourcesTitle' }),
    };
};
